
import { ThemeEvents, CartAddEvent } from '@theme/events';

class CartItemsHelper {
  constructor() {
    this.cartEl = document.querySelector('cart-items-component');
    if (!this.cartEl) {
      console.log('CartItemsHelper: cart-items-component not found');
      return;
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this));
    this.handleOnloadCart();

  }

  addFreeGift(freePromoProduct) {
    // [Archies 2026-09-30] In-flight lock.
    //
    // This method calls handleCartUpdate() again in its own .then(), and that re-reads
    // `free-product-in-cart` from the SERVER-RENDERED progress bar attribute — which has not been
    // re-rendered yet at that moment. So it saw "no gift in cart" and added a second one.
    //
    // It was invisible until the `_gift` mark landed: two adds with identical (empty) properties
    // merged into ONE line at quantity 2, and nothing priced gifts at zero back then. Now each add
    // carries its own timestamp, so they become two lines — and gift-transform zeroes exactly one
    // (chooseGiftLine takes the highest mark and requires quantity 1), leaving the other at full
    // price in the customer's cart.
    if (this._addingGift) return;
    this._addingGift = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionId = this.cartEl.dataset.sectionId;
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    if (freePromoProduct) {
      // Perf #1: bundled section rendering — /cart/add.js with `sections` returns the rendered cart
      // section(s) in the same call (validated against shopify.dev), instead of refetching + parsing the
      // whole page. Mirrors addVariant / the reco add path.
      const sectionIds = Array.from(sectionsToUpdate);
      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: freePromoProduct,
          quantity: 1,
          // [Archies 2026-09-30] Mark the line as the free gift.
          //
          // This is the contract with Archies Promo Engine (shared/gift-core.ts GIFT_PROPERTY):
          //   - promo-fn reads `attribute(key: "_gift")` and SKIPS marked lines before targeting,
          //     so the gift never counts toward a quantity break and is never a discount target.
          //   - gift-transform (cart.transform.run) prices the marked line at zero.
          //
          // Without the mark the gift was just another eligible line: on a 3-item cart it made the
          // count 4, tipped the cart into the "Buy 3 or more" break, and then took $7.50 off the
          // gift itself — a $15 carry bag showing at $7.50 instead of free.
          //
          // The value is a timestamp, not a flag: if a swap is interrupted and two gift lines exist
          // for a moment, both the function and the theme take the HIGHEST value as the real one.
          // An underscore prefix keeps it private, so it never renders as a line-item property.
          properties: { _gift: String(Date.now()) },
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      })
      .then(res => res.json())
      .then(added => {
        if (added && added.status) { console.error('Add free gift failed:', added.description); return; }
        const rendered = added.sections || {};

        sectionIds.forEach(sectionId => {
          const html = rendered[sectionId];
          if (!html) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const drawerNew =
            doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
            doc.querySelector('cart-items-component');
          const drawerOld = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);

          if (!drawerNew || !drawerOld) return;

          const freePromoProductInCartKey = drawerNew.querySelector('free-shipping-component.promo-progress-bar-ext')?.getAttribute('free-promo-product-key');
          const freePromoItem = drawerNew.querySelector(`.cart-items__table-row[data-key="${freePromoProductInCartKey}"]`);
          freePromoItem?.querySelector('.quantity-selector')?.classList.add('hide-this');
          freePromoItem?.querySelector('.cart-items__remove')?.classList.add('hide-this');

          drawerOld.innerHTML = drawerNew.innerHTML;
        });

        this._addingGift = false;
      })
      .catch(err => {
        this._addingGift = false;
        console.error('Error adding free gift:', err);
      });
    } else {
      // Nothing to add — release immediately, or the lock would never lift.
      this._addingGift = false;
    }

  }

  removeFreeGift(freePromoProductInCartKey) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionId = this.cartEl.dataset.sectionId;
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    if (freePromoProductInCartKey) {
      // Perf #1: bundled section rendering — /cart/change.js with `sections` returns the rendered cart
      // section(s) in the same call (validated against shopify.dev), instead of refetching the whole page.
      const sectionIds = Array.from(sectionsToUpdate);
      fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: freePromoProductInCartKey,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(res => res.json())
        .then(changed => {
          const rendered = (changed && changed.sections) || {};
          sectionIds.forEach(sectionId => {
            const html = rendered[sectionId];
            if (!html) return;

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const drawerNew =
              doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
              doc.querySelector('cart-items-component');
            const drawerOld = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);

            if (drawerNew && drawerOld) {
              drawerOld.innerHTML = drawerNew.innerHTML;
            }
          });
        })
        .catch(err => console.error('Error removing free gift:', err));
    }
  }

  /**
   * Add any variant to the cart and refresh the drawer/icon/progress bar, then open the drawer.
   * Reliable, theme-native path (mirrors addFreeGift) — used by the size calculator's
   * "add my size to cart" so it doesn't depend on the PDP buy-buttons being in sync.
   * Returns a promise that rejects if the add fails (e.g. sold out).
   */
  addVariant(variantId, options) {
    options = options || {};
    if (!variantId) return Promise.reject(new Error('no variant id'));

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId && sectionIds.indexOf(item.dataset.sectionId) === -1) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    // Single request: /cart/add.js with `sections` returns the rendered cart section(s) — no full-page fetch.
    const addBody = {
      id: variantId,
      quantity: 1,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname
    };
    // Optional line-item properties (e.g. the size calculator tags its add with `_size_calc`).
    // `_`-prefixed keys are hidden from the customer at checkout but persist on the order.
    if (options.properties && typeof options.properties === 'object') {
      addBody.properties = options.properties;
    }
    return fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addBody)
    })
      .then((res) => res.json())
      .then((added) => {
        if (added && added.status) throw new Error(added.description || 'Add to cart failed');
        // [Archies 2026-10-12] Announce the add exactly as a PDP add does, instead of swapping the
        // cart-items innerHTML by hand. The hand swap only replaced the list, so the drawer kept
        // `cart-drawer--empty` from page load: a first add from the size calculator into an empty
        // cart rendered centred (`.cart-drawer--empty .cart-drawer__content { text-align: center }`)
        // until the next PDP add morphed the section. With the event, CartItemsComponent morphs the
        // whole section (class included), the cart bubble counts the add, and the progress bar /
        // shipping listeners run once each. Shape and source match product-form.js.
        document.dispatchEvent(
          new CartAddEvent({}, String(variantId), {
            source: 'product-form-component',
            itemCount: 1,
            variantId: String(variantId),
            sections: added.sections || {},
          })
        );
      })
      .then(() => {
        if (options.openDrawer !== false) document.querySelector('cart-drawer-component')?.open();
      });
  }

  handleCartUpdate(event) {
    const progressBar = document.querySelector('free-shipping-component.promo-progress-bar-ext');
    if (window.shippingService?.getShippingRates) {
      window.shippingService.getShippingRates();
    }

    setTimeout(() => {
      if (progressBar) {
        
        // [Archies 2026-09-30] TWO GIFT MECHANISMS, AND ONLY ONE RUNS.
        //
        // EVERYDAY (this block, unchanged): settings.free_product_promo +
        // settings.threshhold_total_goal. The bar auto-adds the gift at the threshold and removes it
        // below. This is ordinary trade and must keep behaving exactly as it always has.
        //
        // BFCM (the app): when archies.free_gift is configured and live, the Promo Engine owns the
        // gift. Nothing is auto-added — a tile offers it, the picker adds exactly one marked line,
        // and the guard defaults it on the checkout press if nobody chose. Auto-adding as well
        // would put two gifts in the cart and reintroduce the double-add race.
        //
        // `gift-app-owned` is emitted by free-shipping-component from the app's config, so the
        // handover is decided server-side in one place rather than inferred here.
        const giftAppOwned = progressBar.getAttribute('gift-app-owned') === 'true';

        if (!giftAppOwned) {
          const subTotal = Number(progressBar.getAttribute('cart-items-subtotal'));
          const thresholdGoal = Number(progressBar.getAttribute('threshold-goal'));
          const freePromoProduct = Number(progressBar.getAttribute('free-promo-product'));
          const freePromoProductInCart = progressBar.getAttribute('free-product-in-cart') == 'true';
          const cartItemsIdStr = progressBar.getAttribute('cart-items-id');
          const cartItemsId = cartItemsIdStr.split(',').filter(Boolean).map(Number);

          // The gift step is active only when a gift product is set AND a goal threshold exists.
          // When the gift is sold out, Liquid drops the step and emits threshold-goal="0",
          // so giftActive becomes false and we won't try to add an unavailable gift.
          const giftActive = freePromoProduct && thresholdGoal > 0;

          // [Archies 2026-09-30] The offer window and `status: on` gate the ADD only.
          //
          // NOT giftActive, deliberately. The else-branch below removes the gift whenever the step
          // goes inactive, and during `winding_down` a gift already in a cart must STAY free — that is
          // the entire purpose of winding_down (promo-engine gift-core: "no longer offered, but carts
          // that already hold a gift keep it"). Gating the step would strip exactly the gifts the
          // function is still honouring.
          const giftOffered = progressBar.getAttribute('gift-offered') !== 'false';

          if (giftActive && subTotal >= thresholdGoal) {
            if (!freePromoProductInCart) {
              if (giftOffered) this.addFreeGift(freePromoProduct);
            } else {
              this.hideItemAction(progressBar);
            }
          } else {
            // Below the goal, OR the gift step is inactive (sold out / not configured):
            // remove any stale free gift so it can't linger in the cart.
            if (freePromoProductInCart) {
              const freePromoProductInCartKey = progressBar.getAttribute('free-promo-product-key');
              const freePromoItem = document.querySelector(`.cart-items__table-row[data-key="${freePromoProductInCartKey}"]`);
              freePromoItem?.classList.add('hide-this');
              this.removeFreeGift(freePromoProductInCartKey);
            }
          }

        } else {
          // The app owns it; only keep the line's controls hidden.
          const appGiftInCart = progressBar.getAttribute('free-product-in-cart') == 'true';
          if (appGiftInCart) this.hideItemAction(progressBar);
        }

      }
    }, 10);

  }

  handleOnloadCart(){
    const progressBar = document.querySelector('free-shipping-component.promo-progress-bar-ext');
    this.hideItemAction(progressBar);
  }

  hideItemAction(progressBar){
    const freePromoProductInCartKey = progressBar?.getAttribute('free-promo-product-key');
    const freePromoItem = document.querySelector(`.cart-items__table-row[data-key="${freePromoProductInCartKey}"]`);
    freePromoItem?.querySelector('.quantity-selector')?.classList.add('hide-this');
    freePromoItem?.querySelector('.cart-items__remove')?.classList.add('hide-this');
  }


}

window.CartItemsHelper = new CartItemsHelper();
